import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Eyebrow, Button } from "./Bits";
import { createDonation } from "../lib/api";

const TIERS = [
  { key: "supporter", amount: 10, label: "Supporter", note: "Materials for a youth session" },
  { key: "friend", amount: 25, label: "Friend", note: "A young person at an event" },
  { key: "champion", amount: 50, label: "Champion", note: "Powers a community workshop" },
  { key: "patron", amount: 100, label: "Patron", note: "Backs a whole project day" },
];
const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export default function DonateBlock() {
  const [tier, setTier] = useState("friend");
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ donor_name: "", donor_email: "" });
  const [busy, setBusy] = useState(false);

  const donate = async () => {
    setBusy(true);
    try {
      const payload = { origin_url: window.location.origin, ...donor };
      if (custom) payload.amount = parseFloat(custom);
      else payload.tier = tier;
      const { checkout_url } = await createDonation(payload);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const selected = custom ? `£${custom}` : `£${TIERS.find((t) => t.key === tier).amount}`;

  return (
    <div className="grid lg:grid-cols-[1fr_0.85fr] gap-14 items-start">
      <Reveal>
        <Eyebrow className="text-nead-pink">Choose an amount</Eyebrow>
        <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy">Your gift goes further.</h2>
        <p className="mt-4 text-nead-navy/60 leading-relaxed">As a Community Interest Company, every penny you give is reinvested into our charitable objectives across Cumbria.</p>
        <div className="mt-10 grid grid-cols-2 gap-4">
          {TIERS.map((t) => {
            const active = tier === t.key && !custom;
            return (
              <button key={t.key} onClick={() => { setTier(t.key); setCustom(""); }} data-testid={`donate-tier-${t.key}`}
                className={`text-left rounded-2xl border-2 p-6 transition-all duration-300 ${active ? "border-nead-pink bg-nead-navy text-white" : "border-black/10 bg-white text-nead-navy hover:border-nead-pink"}`}>
                <div className="font-display font-bold text-4xl leading-none">£{t.amount}</div>
                <div className="mt-2 font-semibold">{t.label}</div>
                <div className={`mt-1 text-xs ${active ? "text-white/70" : "text-nead-navy/50"}`}>{t.note}</div>
              </button>
            );
          })}
        </div>
        <div className="mt-6 max-w-xs">
          <label className="text-xs font-semibold uppercase tracking-wider text-nead-navy/50">Or enter your own (£)</label>
          <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount" aria-label="Custom amount" data-testid="donate-custom" className={`${field} mt-2`} />
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="rounded-[1.75rem] bg-nead-mist border border-black/[0.06] p-8">
          <Eyebrow className="text-nead-pink">Your details</Eyebrow>
          <p className="mt-3 text-nead-navy/55 text-sm">Optional, so we can say thank you.</p>
          <div className="mt-7 space-y-4">
            <input value={donor.donor_name} onChange={(e) => setDonor((d) => ({ ...d, donor_name: e.target.value }))} placeholder="Name" aria-label="Name" className={field} data-testid="donate-name" />
            <input type="email" value={donor.donor_email} onChange={(e) => setDonor((d) => ({ ...d, donor_email: e.target.value }))} placeholder="Email" aria-label="Email" className={field} data-testid="donate-email" />
          </div>
          <div className="mt-8">
            <Button onClick={donate} disabled={busy} variant="pink" className="w-full justify-center" testId="donate-submit">
              {busy ? "Redirecting…" : `Donate ${selected}`}
            </Button>
          </div>
          <p className="mt-4 text-center text-xs text-nead-navy/40">Secure payment via Stripe.</p>
        </div>
      </Reveal>
    </div>
  );
}
